import {
  useRef,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  forwardRef,
  cloneElement,
  ReactElement,
  ForwardedRef,
  PropsWithChildren,
  isValidElement,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import clsx from 'clsx';
import { usePopper } from 'react-popper';
import { createPortal } from 'react-dom';
import * as PopperJS from '@popperjs/core';
import { AnimatePresence, motion } from 'motion/react';

import { Arrow } from '../arrow';
import { Shortcut } from '../shortcut';

import { popoverConfig } from '../../constants/ui';

import moduleStyles from './tooltip.module.scss';

export const Tooltip = forwardRef((props: TooltipProps, ref: ForwardedRef<any>) => {
  const {
    icon,
    delay,
    content,
    children,
    shortcut,
    className,
    alwaysOpen,
    arrow = true,
    placement = 'top',
    ignorePointerEvents = true,
  } = props;

  const [open, setOpen] = useState(false);

  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const childRef = isValidElement(children) ? (children.props as { ref?: ForwardedRef<HTMLElement> }).ref ?? null : null;
  const childRefRef = useRef(childRef);
  const forwardedRefRef = useRef(ref);
  const timerId = useRef<ReturnType<typeof setTimeout> | null>(undefined);

  childRefRef.current = childRef;
  forwardedRefRef.current = ref;

  useEffect(() => {
    if (alwaysOpen && content) {
      setOpen(true);
    }
  }, [alwaysOpen, content]);

  const options = {
    placement,
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: placement !== 'right' ? 10 : 0,
        },
      },
      ...popoverConfig([0, 10], 24).modifiers,
    ],
  };

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, options);

  useEffect(() => {
    update?.();
  }, [content, update]);

  const setTriggerRef = useCallback((element: HTMLElement | null) => {
    setReferenceElement((current) => (current === element ? current : element));
    assignRef(childRefRef.current, element);
    assignRef(forwardedRefRef.current, element);
  }, []);

  const onOpen = () => {
    const popoverOpen = referenceElement?.children[0]?.classList.contains('popover-open');
    if (popoverOpen) {
      return;
    }

    if (delay) {
      timerId.current = setTimeout(() => setOpen(true), delay);
      return;
    }
    setOpen(true);
  };

  const onClose = () => {
    if (!alwaysOpen) {
      if (timerId.current) {
        clearTimeout(timerId.current);
        timerId.current = null;
      }
      setOpen(false);
    }
  };

  const show = open && !!content;

  return (
    <>
      {cloneElement(children as ReactElement<any>, {
        ref: setTriggerRef,
        onMouseEnter: onOpen,
        // Some browsers focus a pressed trigger after its pointer-down, which would reopen the tooltip the press
        // just closed. Only keyboard focus opens it (checked on the focused element, since the trigger may wrap it);
        // pointer users already get it on hover.
        onFocus: (event: FocusEvent<HTMLElement>) => {
          if ((event.target as Element).matches(':focus-visible')) {
            onOpen();
          }
        },
        onBlur: onClose,
        onMouseLeave: onClose,
        // A pressed trigger may capture the pointer or hide itself, so it may never get the mouse-leave that
        // would close the tooltip. The click close also covers keyboard activation, which has no pointer-down.
        onPointerDown: (event: PointerEvent) => {
          onClose();
          (children as ReactElement<{ onPointerDown?: (event: PointerEvent) => void }>).props.onPointerDown?.(event);
        },
        onClick: (event: MouseEvent) => {
          onClose();
          (children as ReactElement<{ onClick?: (event: MouseEvent) => void }>).props.onClick?.(event);
        },
      })}
      <AnimatePresence>
        {show && (
          <>
            {createPortal(
              <motion.div
                initial={{
                  opacity: 0,
                  translateY: -10,
                }}
                animate={{
                  opacity: 1,
                  translateY: 0,
                  transition: {
                    duration: 0.4,
                  },
                }}
                exit={{
                  opacity: 0,
                  translateY: -10,
                }}
                ref={setPopperElement}
                className={clsx(moduleStyles.tooltipHolder, {
                  'pointer-events-none': ignorePointerEvents,
                })}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={onClose}
                style={styles.popper}
                {...attributes.popper}
              >
                <div className={moduleStyles.tooltip}>
                  <div className={clsx(moduleStyles.content, className)} ref={contentRef}>
                    {content}
                    {icon}
                    {!!shortcut && <Shortcut data={shortcut} size="small" />}
                  </div>
                  {arrow && (
                    <Arrow
                      size="medium"
                      ref={setArrowElement}
                      style={styles.arrow}
                      attributes={{
                        'data-popper-placement': attributes.popper?.['data-popper-placement'],
                      }}
                    />
                  )}
                </div>
              </motion.div>,
              document.body
            )}
          </>
        )}
      </AnimatePresence>
    </>
  );
});

interface TooltipProps extends PropsWithChildren {
  delay?: number;
  arrow?: boolean;
  icon?: ReactNode;
  shortcut?: string;
  className?: string;
  content?: ReactNode;
  alwaysOpen?: boolean;
  ignorePointerEvents?: boolean;
  placement?: PopperJS.Placement;
}

const assignRef = <T,>(ref: ForwardedRef<T>, value: T | null) => {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  ref.current = value;
};
